import React, { useState, useEffect, useMemo } from "react";
import { supabase, fetchWithTimeout } from "@/lib/supabase";
import { useSearchParams } from "react-router-dom";
import Navbar from "../home/components/Navbar";
import Hero from "../home/components/Hero";
import Footer from "../home/components/Footer";
import ProductCard from "@/components/ProductCard";
import AkakceFilterSidebar, { FilterState } from "@/components/AkakceFilterSidebar";
import { verifiedProducts } from "@/mocks/products";

// Hızlı Kategori Kartları (Ana Sayfa Vitrini İçin)
const FEATURED_CATEGORIES = [
  { id: "erkek-tisort", label: "Tişört & Polo", icon: "ri-t-shirt-line", badge: "%50'ye Varan" },
  { id: "erkek-spor-ayakkabi", label: "Spor Ayakkabı", icon: "ri-footprint-line", badge: "İndirimde" },
  { id: "erkek-gomlek", label: "Gömlek", icon: "ri-shirt-line", badge: "Outlet" },
  { id: "erkek-pantolon", label: "Pantolon & Jean", icon: "ri-pantone-line", badge: "Fırsat" },
  { id: "elektronik", label: "Telefon & Saat", icon: "ri-smartphone-line", badge: "En Ucuz" },
  { id: "parfum", label: "Parfüm & Kozmetik", icon: "ri-magic-line", badge: "Popüler" },
  { id: "ev-yasam", label: "Airfryer & Mutfak", icon: "ri-home-4-line", badge: "Trend" },
  { id: "erkek-mont", label: "Mont & Dış Giyim", icon: "ri-contrast-drop-2-line", badge: "Sezon Sonu" },
];

export default function HomePage() {
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortBy, setSortBy] = useState<string>("featured");
  const [userFavs, setUserFavs] = useState<string[]>([]);
  const [userAlarms, setUserAlarms] = useState<string[]>([]);

  const categoryParam = searchParams.get("category") || "all";
  const searchParam = searchParams.get("q") || "";

  // Akakçe Filtre State'i
  const [filters, setFilters] = useState<FilterState>({
    category: categoryParam,
    gender: [],
    sizes: [],
    brands: [],
    collarTypes: [],
    fits: [],
    minPrice: "",
    maxPrice: "",
    minDiscount: null,
    stores: [],
    searchKeyword: searchParam,
  });

  // URL Parametresi Değiştiğinde Senkronize Et
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      category: categoryParam,
      searchKeyword: searchParam,
    }));
  }, [categoryParam, searchParam]);

  // Favoriler ve Alarmları Çek
  useEffect(() => {
    async function loadUserData() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: favs } = await supabase.from("user_favorites").select("product_id").eq("user_id", user.id);
          if (favs) setUserFavs(favs.map((f: any) => f.product_id));

          const { data: alarms } = await supabase.from("price_alerts").select("product_id").eq("user_id", user.id);
          if (alarms) setUserAlarms(alarms.map((a: any) => a.product_id));
        }
      } catch {}
    }
    loadUserData();
  }, []);

  // Supabase'den Tüm İndirimli Ürünleri Çek (Hata durumunda yerel zırhlı kataloğu kullan)
  useEffect(() => {
    async function fetchProducts() {
      setLoading(true);
      try {
        let list: any[] | null = null;
        try {
          const query = supabase
            .from("products")
            .select("*")
            .eq("aktif_mi", true)
            .order("id", { ascending: true });

          const { data, error }: any = await fetchWithTimeout(query as any, 600);

          if (!error && data && data.length > 0) {
            list = data;
          }
        } catch {
          list = null;
        }

        if (!list || list.length === 0) {
          list = verifiedProducts;
        }

        setProducts(list);
      } catch (err) {
        console.error("Ürünler yüklenirken hata:", err);
        setProducts(verifiedProducts);
      } finally {
        setLoading(false);
      }
    }
    fetchProducts();
  }, []);

  // Kullanıcı bir kategori seçti mi veya arama yaptı mı?
  const isCategoryOrSearchActive = filters.category !== "all" || Boolean(filters.searchKeyword.trim());

  // === KESİN KATEGORİ EŞLEŞTİRME FONKSİYONU ===
  const isProductMatchingCategory = (targetCat: string, p: any) => {
    if (!targetCat || targetCat === "all") return true;
    const c = targetCat.toLowerCase();
    const pCat = (p.category || "").toLowerCase();
    const pTitle = (p.title || "").toLowerCase();
    const pGender = (p.gender || p.cinsiyet || "unisex").toLowerCase();

    const isMaleTarget = c.includes("erkek") || c.startsWith("erkek");
    const isFemaleTarget = c.includes("kadin") || c.includes("kadın") || c.startsWith("kadin");

    const isExplicitFemale = pGender === "kadin" || pGender === "kadın" || pCat.includes("kadin") || pTitle.includes("kadın");
    const isExplicitMale = pGender === "erkek" || pCat.includes("erkek") || pTitle.includes("erkek");

    // Kesin Cinsiyet İzolasyonu
    if (isMaleTarget && isExplicitFemale && !pGender.includes("unisex")) return false;
    if (isFemaleTarget && isExplicitMale && !pGender.includes("unisex")) return false;

    // 1. TİŞÖRT & POLO
    if (c.includes("tisort") || c.includes("polo") || c.includes("t-shirt")) {
      const isTshirt = pCat.includes("tisort") || pCat.includes("tişört") || pTitle.includes("tişört") || pTitle.includes("t-shirt") || pTitle.includes("polo");
      if (!isTshirt) return false;
      return true;
    }

    // 2. GÖMLEK
    if (c.includes("gomlek") || c.includes("gömlek") || c.includes("bluz")) {
      const isShirt = pCat.includes("gomlek") || pCat.includes("gömlek") || pTitle.includes("gömlek") || pTitle.includes("gomlek") || pTitle.includes("bluz");
      if (!isShirt) return false;
      if (c.includes("erkek") && !pGender.includes("erkek") && !pGender.includes("unisex") && !pTitle.includes("erkek")) return false;
      if (c.includes("kadin") && !pGender.includes("kadin") && !pGender.includes("unisex") && !pTitle.includes("kadın")) return false;
      return true;
    }

    // 3. PANTOLON & JEAN
    if (c.includes("pantolon") || c.includes("jean") || c.includes("chino") || c.includes("denim")) {
      const isPants = pCat.includes("pantolon") || pCat.includes("jean") || pTitle.includes("pantolon") || pTitle.includes("jean") || pTitle.includes("chino");
      if (!isPants) return false;
      if (c.includes("erkek") && !pGender.includes("erkek") && !pGender.includes("unisex") && !pTitle.includes("erkek")) return false;
      if (c.includes("kadin") && !pGender.includes("kadin") && !pGender.includes("unisex") && !pTitle.includes("kadın")) return false;
      return true;
    }

    // 4. TAKIM ELBİSE
    if (c.includes("takim") || c.includes("smokin")) {
      return pCat.includes("takim") || pTitle.includes("takım") || pTitle.includes("smokin");
    }

    // 5. SWEATSHIRT
    if (c.includes("sweat") || c.includes("hoodie")) {
      return pCat.includes("sweat") || pTitle.includes("sweat") || pTitle.includes("kapüşonlu");
    }

    // 6. KAZAK & TRİKO
    if (c.includes("kazak") || c.includes("triko") || c.includes("hirka")) {
      return pCat.includes("kazak") || pCat.includes("triko") || pTitle.includes("kazak") || pTitle.includes("triko") || pTitle.includes("hırka");
    }

    // 7. MONT & DIŞ GİYİM
    if (c.includes("mont") || c.includes("kaban") || c.includes("ceket")) {
      return pCat.includes("mont") || pCat.includes("kaban") || pTitle.includes("mont") || pTitle.includes("kaban") || pTitle.includes("ceket");
    }

    // 8. AYAKKABI & SNEAKER
    if (c.includes("ayakkabi") || c.includes("sneaker") || c.includes("bot")) {
      const isShoe = pCat.includes("ayakkabi") || pCat.includes("sneaker") || pTitle.includes("ayakkabı") || pTitle.includes("sneaker") || pTitle.includes("bot");
      if (!isShoe) return false;
      if (c.includes("erkek") && !pGender.includes("erkek") && !pGender.includes("unisex") && !pTitle.includes("erkek")) return false;
      if (c.includes("kadin") && !pGender.includes("kadin") && !pGender.includes("unisex") && !pTitle.includes("kadın")) return false;
      return true;
    }

    // 9. ELEKTRONİK
    if (c.includes("telefon") || c.includes("phone")) {
      return pCat.includes("telefon") || pTitle.includes("telefon") || pTitle.includes("iphone");
    }
    if (c.includes("saat") || c.includes("watch")) {
      return pCat.includes("saat") || pTitle.includes("saat") || pTitle.includes("watch");
    }
    if (c.includes("kulaklik") || c.includes("ses")) {
      return pCat.includes("kulaklik") || pTitle.includes("kulaklık") || pTitle.includes("hoparlör") || pTitle.includes("jbl") || pTitle.includes("sony");
    }
    if (c.includes("tablet") || c.includes("ipad")) {
      return pCat.includes("tablet") || pTitle.includes("tablet") || pTitle.includes("ipad");
    }
    if (c.includes("elektronik")) {
      return pCat.includes("elektronik") || pCat.includes("telefon") || pCat.includes("saat") || pCat.includes("tablet") || pTitle.includes("telefon") || pTitle.includes("saat") || pTitle.includes("kulaklık") || pTitle.includes("ipad");
    }

    // 10. KOZMETİK & PARFÜM
    if (c.includes("parfum") || c.includes("parfüm")) {
      return pCat.includes("parfum") || pCat.includes("parfüm") || pTitle.includes("parfüm") || pTitle.includes("parfum") || pTitle.includes("sauvage") || pTitle.includes("chanel");
    }
    if (c.includes("kozmetik") || c.includes("cilt")) {
      return pCat.includes("kozmetik") || pCat.includes("parfum") || pCat.includes("cilt") || pTitle.includes("parfüm") || pTitle.includes("krem") || pTitle.includes("serum");
    }

    // 11. EV & YAŞAM
    if (c.includes("airfryer") || c.includes("fritoz")) {
      return pCat.includes("airfryer") || pTitle.includes("airfryer") || pTitle.includes("fritöz");
    }
    if (c.includes("kahve")) {
      return pCat.includes("kahve") || pTitle.includes("kahve");
    }
    if (c.includes("supurge") || c.includes("süpürge")) {
      return pCat.includes("supurge") || pTitle.includes("süpürge") || pTitle.includes("dyson");
    }
    if (c.includes("ev-yasam") || c.includes("evyasam")) {
      return pCat.includes("ev") || pCat.includes("airfryer") || pCat.includes("kahve") || pCat.includes("supurge") || pTitle.includes("airfryer") || pTitle.includes("kahve") || pTitle.includes("süpürge");
    }

    // 12. GENEL KADIN / ERKEK
    if (c === "kadin") {
      return pGender.includes("kadin") || pGender.includes("unisex") || pCat.includes("kadin") || pTitle.includes("kadın");
    }
    if (c === "erkek") {
      return pGender.includes("erkek") || pGender.includes("unisex") || pCat.includes("erkek") || pTitle.includes("erkek");
    }

    return pCat.includes(c) || pTitle.includes(c);
  };

  // Akakçe Tarzı Çoklu Filtreleme Motoru
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      // 1. Kategori Filtresi
      if (filters.category !== "all") {
        if (!isProductMatchingCategory(filters.category, p)) return false;
      }

      // 2. Arama Kelimesi Filtresi
      if (filters.searchKeyword.trim()) {
        const kw = filters.searchKeyword.toLowerCase();
        const matchTitle = (p.title || "").toLowerCase().includes(kw);
        const matchBrand = (p.brand || "").toLowerCase().includes(kw);
        const matchCat = (p.category || "").toLowerCase().includes(kw);
        if (!matchTitle && !matchBrand && !matchCat) return false;
      }

      // 3. Cinsiyet Filtresi
      if (filters.gender.length > 0) {
        const pGender = (p.gender || p.cinsiyet || "Unisex").toLowerCase();
        const pTitle = (p.title || "").toLowerCase();
        const matchesGender = filters.gender.some((g) => {
          const target = g.toLowerCase();
          return pGender.includes(target) || pTitle.includes(target) || (target === "unisex" && pGender === "unisex");
        });
        if (!matchesGender) return false;
      }

      // 4. Beden / Numara Filtresi
      if (filters.sizes.length > 0) {
        const pSizes = Array.isArray(p.bedenler) ? p.bedenler.join(" ").toLowerCase() : (p.beden || p.bedenler || "").toString().toLowerCase();
        const pTitle = (p.title || "").toLowerCase();
        const matchesSize = filters.sizes.some((sz) => {
          const s = sz.toLowerCase();
          return pSizes.includes(s) || pTitle.includes(s);
        });
        if (!matchesSize) return false;
      }

      // 5. Marka Filtresi
      if (filters.brands.length > 0) {
        const pBrand = (p.brand || "").toLowerCase();
        const pTitle = (p.title || "").toLowerCase();
        const matchesBrand = filters.brands.some((b) => {
          const br = b.toLowerCase();
          return pBrand.includes(br) || pTitle.includes(br);
        });
        if (!matchesBrand) return false;
      }

      // 6. Yaka Tipi Filtresi
      if (filters.collarTypes.length > 0) {
        const pTitle = (p.title || "").toLowerCase();
        const matchesCollar = filters.collarTypes.some((c) => pTitle.includes(c.toLowerCase().replace(" yaka", "")));
        if (!matchesCollar) return false;
      }

      // 7. Kalıp / Fit Filtresi
      if (filters.fits.length > 0) {
        const pTitle = (p.title || "").toLowerCase();
        const matchesFit = filters.fits.some((f) => pTitle.includes(f.toLowerCase().replace(" fit", "")));
        if (!matchesFit) return false;
      }

      // 8. Fiyat Aralığı
      const price = p.current_price || p.price || 0;
      if (filters.minPrice && price < parseFloat(filters.minPrice)) return false;
      if (filters.maxPrice && price > parseFloat(filters.maxPrice)) return false;

      // 9. Minimum İndirim Oranı
      if (filters.minDiscount && (p.discount_rate || 0) < filters.minDiscount) return false;

      // 10. Mağaza Filtresi
      if (filters.stores.length > 0) {
        const pSource = (p.source || p.source_url || "").toLowerCase();
        const matchesStore = filters.stores.some((st) => pSource.includes(st.toLowerCase()));
        if (!matchesStore && !pSource.includes("trendyol")) return false;
      }

      return true;
    });

    // Keep the first listing for each product title or canonical store URL.
    const seenTitles = new Set<string>();
    const seenUrls = new Set<string>();
    const uniqueList: any[] = [];
    for (const p of filtered) {
      const normalizedTitle = (p.title || p.name || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
      const rawUrl = p.product_url || p.affiliate_url || p.affiliate_link;
      let normalizedUrl = "";
      if (rawUrl && rawUrl !== "#") {
        try {
          const url = new URL(rawUrl);
          normalizedUrl = `${url.hostname.toLowerCase()}${url.pathname.replace(/\/$/, "")}`;
        } catch {
          normalizedUrl = rawUrl.trim().toLowerCase().split(/[?#]/, 1)[0];
        }
      }

      if ((normalizedTitle && seenTitles.has(normalizedTitle)) || (normalizedUrl && seenUrls.has(normalizedUrl))) continue;
      if (normalizedTitle) seenTitles.add(normalizedTitle);
      if (normalizedUrl) seenUrls.add(normalizedUrl);
      uniqueList.push(p);
    }
    return uniqueList;
  }, [products, filters]);

  // Sıralama Motoru
  const sortedProducts = useMemo(() => {
    const list = [...filteredProducts];
    if (sortBy === "price_asc") {
      return list.sort((a, b) => (a.current_price || 0) - (b.current_price || 0));
    }
    if (sortBy === "price_desc") {
      return list.sort((a, b) => (b.current_price || 0) - (a.current_price || 0));
    }
    if (sortBy === "discount_desc") {
      return list.sort((a, b) => (b.discount_rate || 0) - (a.discount_rate || 0));
    }
    if (sortBy === "rating_desc") {
      return list.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    }
    return list;
  }, [filteredProducts, sortBy]);

  const handleResetFilters = () => {
    setSearchParams({});
    setFilters({
      category: "all",
      gender: [],
      sizes: [],
      brands: [],
      collarTypes: [],
      fits: [],
      minPrice: "",
      maxPrice: "",
      minDiscount: null,
      stores: [],
      searchKeyword: "",
    });
  };

  const handleSelectCategory = (catId: string) => {
    setSearchParams({ category: catId });
    setFilters((prev) => ({
      ...prev,
      category: catId,
    }));
  };

  // Dinamik Akakçe Sayfa Başlığı
  const getPageHeading = () => {
    if (filters.searchKeyword) return `"${filters.searchKeyword}" İndirimli Fiyatları & Modelleri`;
    const c = filters.category.toLowerCase();
    if (c === "erkek-tisort") return "Erkek Tişört & Polo İndirimli Fiyatları";
    if (c === "kadin-tisort") return "Kadın Tişört & Top İndirimli Fiyatları";
    if (c === "tisort") return "Tişört & Polo İndirimli Fiyatları";
    if (c.includes("gomlek")) return "Gömlek & Bluz İndirimli Modeller";
    if (c.includes("pantolon") || c.includes("jean")) return "Pantolon & Jean Fiyatları";
    if (c.includes("takim")) return "Erkek Takım Elbise & Smokin Fırsatları";
    if (c.includes("sweat")) return "Sweatshirt & Hoodie Outlet Fırsatları";
    if (c.includes("kazak") || c.includes("triko")) return "Kazak & Triko İndirimleri";
    if (c.includes("mont") || c.includes("kaban")) return "Mont & Dış Giyim Fiyatları";
    if (c.includes("ayakkabi") || c.includes("sneaker")) return "Spor Ayakkabı & Sneaker En Ucuz Fiyatları";
    if (c.includes("telefon")) return "Cep Telefonu & Akıllı Telefon İndirimleri";
    if (c.includes("saat")) return "Akıllı Saat & Saat Fiyatları";
    if (c.includes("kulaklik")) return "Kulaklık & Ses Sistemleri İndirimleri";
    if (c.includes("tablet")) return "Tablet & Bilgisayar İndirimleri";
    if (c.includes("elektronik")) return "Elektronik & Akıllı Cihaz Fırsatları";
    if (c.includes("parfum") || c.includes("parfüm")) return "Parfüm & Deodorant En Ucuz Fiyatları";
    if (c.includes("kozmetik") || c.includes("cilt")) return "Kozmetik & Cilt Bakımı Fırsatları";
    if (c.includes("airfryer")) return "Airfryer Sıcak Hava Fritözleri En Ucuz Fiyatları";
    if (c.includes("kahve")) return "Kahve Makineleri İndirimli Fiyatları";
    if (c.includes("supurge")) return "Dikey & Robot Süpürge İndirimleri";
    if (c.includes("ev")) return "Ev & Mutfak Aletleri İndirimleri";
    if (c === "kadin") return "Kadın Outlet & İndirimli Ürün Fırsatları";
    if (c === "erkek") return "Erkek Outlet & İndirimli Ürün Fırsatları";
    return "Günün En İyi Outlet & İndirim Fırsatları";
  };

  return (
    <div className="min-h-screen bg-[#f4f6f9] text-gray-900 font-sans">
      <Navbar />

      {/* Hero Alanı (Yalnızca ana sayfadayken göster) */}
      {!isCategoryOrSearchActive && <Hero />}

      <main className="max-w-[1440px] mx-auto px-4 lg:px-8 py-8">
        
        {/* --- 1. ANA SAYFA VİTRİNİ (KATEGORİ SEÇİLİ DEĞİLKEN) --- */}
        {!isCategoryOrSearchActive && (
          <div className="mb-10">
            {/* Popüler Kategori Butonları */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-black text-gray-900 tracking-tight flex items-center gap-2">
                  <i className="ri-fire-fill text-rose-600"></i> Popüler İndirim Kategorileri
                </h2>
                <span className="text-xs text-gray-400 font-bold">Kategoriye tıklayarak detaylı filtreleyin</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
                {FEATURED_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => handleSelectCategory(cat.id)}
                    className="flex flex-col items-center justify-center p-3.5 bg-white rounded-2xl border border-gray-200 hover:border-rose-500 hover:shadow-md hover:-translate-y-0.5 transition-all group cursor-pointer text-center"
                  >
                    <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center text-xl mb-2 group-hover:bg-rose-600 group-hover:text-white transition-colors">
                      <i className={cat.icon}></i>
                    </div>
                    <span className="text-xs font-bold text-gray-800 group-hover:text-rose-600 line-clamp-1">
                      {cat.label}
                    </span>
                    <span className="text-[10px] text-gray-400 font-semibold mt-0.5">
                      {cat.badge}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Karışık Fırsatlar Başlık & Sıralama */}
            <div className="bg-white p-5 rounded-3xl border border-gray-200 shadow-sm mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl md:text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2">
                  <span>🔥 Günün İndirimli Fırsatları</span>
                  <span className="text-xs font-bold bg-rose-50 text-rose-600 px-2.5 py-1 rounded-full border border-rose-200">
                    {sortedProducts.length} Ürün
                  </span>
                </h2>
                <p className="text-xs text-gray-500 mt-1 font-medium">
                  Tüm kategorilerden Türkiye'deki en ucuz outlet ve indirim fırsatları karışık listeleniyor.
                </p>
              </div>

              <div className="flex items-center gap-2 self-end md:self-auto">
                <span className="text-xs font-bold text-gray-400">Sırala:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="text-xs font-black bg-gray-50 border border-gray-200 text-gray-800 rounded-xl px-3 py-2 focus:outline-none focus:border-rose-500 cursor-pointer shadow-xs"
                >
                  <option value="discount_desc">En Yüksek İndirim Oranı (% İndirim)</option>
                  <option value="price_asc">En Düşük Fiyat (En Ucuz)</option>
                  <option value="price_desc">En Yüksek Fiyat</option>
                  <option value="rating_desc">En Çok Beğenilenler</option>
                </select>
              </div>
            </div>

            {/* Tam Genişlikte 4'lü Grid Ürün Listesi */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 animate-pulse">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <div key={n} className="h-96 bg-white rounded-3xl border border-gray-200"></div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {sortedProducts.map((item) => (
                  <ProductCard
                    key={item.id}
                    product={item}
                    initialIsFavorite={userFavs.includes(item.id)}
                    initialIsAlarmSet={userAlarms.includes(item.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* --- 2. KATEGORİ VEYA ARAMA SEÇİLİ OLDUĞUNDA (AKAKÇE FİLTRELEME DÜZENİ) --- */}
        {isCategoryOrSearchActive && (
          <div>
            {/* Akakçe Başlık & Sayaç & Sıralama Barı */}
            <div className="bg-white p-5 rounded-3xl border border-gray-200 shadow-sm mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 text-xs text-gray-400 font-bold mb-1">
                  <button onClick={handleResetFilters} className="hover:text-rose-600 transition-colors cursor-pointer">
                    Ana Sayfa
                  </button>
                  <span>/</span>
                  <span className="text-rose-600 capitalize">{filters.category}</span>
                </div>
                <h1 className="text-xl md:text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2">
                  <span>{getPageHeading()}</span>
                  <span className="text-xs font-bold bg-rose-50 text-rose-600 px-2.5 py-1 rounded-full border border-rose-200">
                    {sortedProducts.length} İndirimli Ürün
                  </span>
                </h1>
                <p className="text-xs text-gray-500 mt-1 font-medium">
                  Trendyol, Hepsiburada, Amazon, D'S Damat, LCW ve yetkili mağazalardaki en ucuz fiyatlar karşılaştırılıyor.
                </p>
              </div>

              {/* Akakçe Sıralama Seçimi */}
              <div className="flex items-center gap-2 self-end md:self-auto">
                <span className="text-xs font-bold text-gray-400">Sırala:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="text-xs font-black bg-gray-50 border border-gray-200 text-gray-800 rounded-xl px-3 py-2 focus:outline-none focus:border-rose-500 cursor-pointer shadow-xs"
                >
                  <option value="discount_desc">En Yüksek İndirim Oranı (% İndirim)</option>
                  <option value="price_asc">En Düşük Fiyat (En Ucuz)</option>
                  <option value="price_desc">En Yüksek Fiyat</option>
                  <option value="rating_desc">En Yüksek Puanlılar</option>
                </select>
              </div>
            </div>

            {/* Seçili Filtre Çipleri (Pills) */}
            {(filters.gender.length > 0 || filters.sizes.length > 0 || filters.brands.length > 0 || filters.collarTypes.length > 0 || filters.fits.length > 0 || filters.minDiscount) && (
              <div className="flex items-center gap-2 flex-wrap mb-6 bg-white p-3.5 rounded-2xl border border-gray-200">
                <span className="text-xs font-black text-gray-400 mr-1">Seçili Filtreler:</span>
                {filters.gender.map((g) => (
                  <button
                    key={g}
                    onClick={() => setFilters({ ...filters, gender: filters.gender.filter((x) => x !== g) })}
                    className="text-xs font-bold bg-gray-100 hover:bg-rose-50 text-gray-700 hover:text-rose-600 px-3 py-1 rounded-xl border border-gray-200 flex items-center gap-1.5 transition-all cursor-pointer"
                  >
                    <span>{g}</span>
                    <i className="ri-close-line"></i>
                  </button>
                ))}
                {filters.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setFilters({ ...filters, sizes: filters.sizes.filter((x) => x !== s) })}
                    className="text-xs font-bold bg-rose-50 text-rose-700 px-3 py-1 rounded-xl border border-rose-200 flex items-center gap-1.5 transition-all cursor-pointer"
                  >
                    <span>Beden: {s}</span>
                    <i className="ri-close-line"></i>
                  </button>
                ))}
                {filters.brands.map((b) => (
                  <button
                    key={b}
                    onClick={() => setFilters({ ...filters, brands: filters.brands.filter((x) => x !== b) })}
                    className="text-xs font-bold bg-blue-50 text-blue-700 px-3 py-1 rounded-xl border border-blue-200 flex items-center gap-1.5 transition-all cursor-pointer"
                  >
                    <span>Marka: {b}</span>
                    <i className="ri-close-line"></i>
                  </button>
                ))}
                {filters.minDiscount && (
                  <button
                    onClick={() => setFilters({ ...filters, minDiscount: null })}
                    className="text-xs font-bold bg-emerald-50 text-emerald-700 px-3 py-1 rounded-xl border border-emerald-200 flex items-center gap-1.5 transition-all cursor-pointer"
                  >
                    <span>%{filters.minDiscount}+ İndirim</span>
                    <i className="ri-close-line"></i>
                  </button>
                )}
                <button
                  onClick={handleResetFilters}
                  className="text-xs font-black text-rose-600 hover:underline ml-auto cursor-pointer"
                >
                  Filtreleri Temizle
                </button>
              </div>
            )}

            {/* Sol Akakçe Filtre Paneli + Sağ 3 Kolon Ürün Listesi */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 items-start">
              {/* Sol Kolon: Akakçe Filtreleme Paneli */}
              <div className="lg:col-span-1">
                <AkakceFilterSidebar
                  filters={filters}
                  onChange={setFilters}
                  onReset={handleResetFilters}
                  totalResultsCount={sortedProducts.length}
                />
              </div>

              {/* Sağ Kolon: Akakçe Fiyat Karşılaştırmalı Ürün Kartları */}
              <div className="lg:col-span-3">
                {loading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 animate-pulse">
                    {[1, 2, 3, 4, 5, 6].map((n) => (
                  <div key={n} className="h-96 bg-white rounded-3xl border border-gray-200"></div>
                ))}
              </div>
            ) : sortedProducts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {sortedProducts.map((item) => (
                  <ProductCard
                    key={item.id}
                    product={item}
                    initialIsFavorite={userFavs.includes(item.id)}
                    initialIsAlarmSet={userAlarms.includes(item.id)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-white rounded-3xl border border-gray-200 p-8 shadow-sm">
                <div className="w-16 h-16 bg-rose-50 text-rose-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                  <i className="ri-filter-off-line"></i>
                </div>
                <h3 className="text-lg font-black text-gray-900">Aradığınız Kriterlerde İndirimli Ürün Bulunamadı</h3>
                <p className="text-xs text-gray-500 mt-1.5 max-w-md mx-auto">
                  Seçtiğiniz beden, marka veya filtrelerde şu an aktif indirimli ürün bulunmuyor. Filtreleri temizleyerek tüm fırsatları görebilirsiniz.
                </p>
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="mt-5 px-6 py-2.5 bg-gray-900 hover:bg-rose-600 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-sm cursor-pointer"
                >
                  Filtreleri Temizle
                </button>
              </div>
            )}
              </div>
            </div>
          </div>
        )}

      </main>

      <Footer />
    </div>
  );
}
